<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="820" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-form class="fdlg__form" @submit.prevent="save">
        <div class="fdlg__head">
          <div>
            <h2 class="fdlg__title">Corectează și retrimite</h2>
            <p class="fdlg__sub">Puteți corecta o singură dată; demersul revine la Șeful departamentului.</p>
          </div>
          <v-btn icon aria-label="Închide" @click="close()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text ref="body" class="fdlg__body">
          <div v-if="rejection" class="rejection">
            <div class="rejection__head">
              <v-icon small>mdi-close-octagon-outline</v-icon>
              Respins de {{ rejection.user }}<span v-if="rejection.date"> · {{ formatDate(rejection.date) }}</span>
            </div>
            <div class="rejection__text">{{ rejection.comment }}</div>
          </div>

          <section v-if="details" class="form-section">
            <div class="form-section__label"><v-icon small>mdi-desktop-classic</v-icon>{{ details.equipment }}</div>
            <p v-if="!details.createsEquipment" class="form-section__note">
              {{ details.model }}<span v-if="details.registration_number"> · nr. {{ details.registration_number }}</span>
              — echipamentul nu se schimbă, doar cauza.
            </p>
            <v-row dense>
              <v-col cols="12">
                <v-select
                  v-model="causeId"
                  :items="causes"
                  label="Tip cerere*"
                  item-text="type"
                  item-value="id"
                  prepend-inner-icon="mdi-format-list-bulleted-type"
                  outlined
                  :error-messages="errors.causeId"
                ></v-select>
              </v-col>

              <template v-if="details.createsEquipment">
                <v-col cols="12" sm="7">
                  <v-combobox
                    v-model="model"
                    :search-input.sync="modelSearch"
                    :items="modelSuggestions"
                    label="Model*"
                    prepend-inner-icon="mdi-tag-outline"
                    outlined
                    :menu-props="{ offsetY: true, contentClass: 'menu-wrap' }"
                    :error-messages="errors.model"
                  ></v-combobox>
                </v-col>
                <v-col cols="12" sm="5">
                  <v-text-field
                    v-model="registration"
                    label="Nr. inventar (opțional)"
                    prepend-inner-icon="mdi-barcode"
                    outlined
                    maxlength="150"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-autocomplete
                    ref="location"
                    v-model="locationId"
                    :items="locations"
                    label="Unde se livrează*"
                    item-text="label"
                    item-value="id"
                    prepend-inner-icon="mdi-map-marker-outline"
                    outlined
                    :menu-props="locationMenuProps"
                    :error-messages="errors.locationId"
                    @focus="fitLocationMenu"
                  ></v-autocomplete>
                </v-col>
              </template>
            </v-row>
          </section>

          <section class="form-section">
            <div class="form-section__label"><v-icon small>mdi-message-text-outline</v-icon>Ce ați corectat*</div>
            <v-textarea
              v-model="comments"
              placeholder="Explicați pe scurt ce ați schimbat, pentru Șef și Contabilitate"
              outlined
              auto-grow
              rows="3"
              maxlength="2000"
              :error-messages="errors.comments"
            ></v-textarea>
            <PhotoInput
              v-model="photos"
              purpose="comment"
              :max="6"
              label="Fotografii (opțional)"
              hint="De exemplu eticheta cu nr. de inventar; le văd Șeful și Contabilitatea."
              @uploading="photosUploading = $event"
              @error="$emit('error', $event)"
            />
          </section>
        </v-card-text>

        <div class="fdlg__foot">
          <v-spacer></v-spacer>
          <v-btn text large @click="close()">Anulare</v-btn>
          <v-btn class="btn-green" large type="submit" :loading="saving" :disabled="!details || photosUploading">
            <v-icon left>mdi-send-outline</v-icon>Retrimite demersul
          </v-btn>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper";
import PhotoInput from "@/components/files/PhotoInput.vue";

// The author's one correction after Contabilitate rejected: the cause (same type, same kind: existing / new item),
// and for a new item also model, nr. inventar and room. The server sends it back to the Șef.
export default {
  components: { PhotoInput },
  data() {
    return {
      dialog: false,
      saving: false,
      photos: [],
      photosUploading: false,
      details: null,
      rejection: null,
      causes: [],
      modelSuggestions: [],
      locations: [],
      locationMenuWidth: null,
      causeId: null,
      model: null,
      modelSearch: null,
      registration: "",
      locationId: null,
      comments: "",
      errors: { causeId: "", model: "", locationId: "", comments: "" },
    };
  },
  computed: {
    modelText() {
      const text = this.modelSearch != null ? this.modelSearch : this.model;
      return typeof text === "string" ? text.trim() : "";
    },
    locationMenuProps() {
      return {
        offsetY: true,
        offsetOverflow: true,
        maxWidth: this.locationMenuWidth || "auto",
        contentClass: "menu-wrap",
      };
    },
  },
  watch: {
    causeId() {
      this.errors.causeId = "";
    },
    modelSearch() {
      this.errors.model = "";
    },
    locationId() {
      this.errors.locationId = "";
    },
    comments() {
      this.errors.comments = "";
    },
  },
  methods: {
    // resolves false (and says why) when the request can no longer be corrected
    async open(requestId) {
      try {
        const details = await api.getRequestDetails(requestId);
        if (!details || !details.actions || !details.actions.correct) {
          this.$emit("error", "Acest demers nu mai poate fi corectat.");
          return false;
        }
        const [causes, history, suggestions, locations] = await Promise.all([
          api.fetchRequestTypes(),
          api.getRequestCommentsHistory(requestId).catch(() => []),
          details.createsEquipment ? api.getModelSuggestions(details.equipmentTypeId) : [],
          details.createsEquipment ? api.fetchLocations() : [],
        ]);
        this.reset();
        this.details = details;
        this.rejection = [...history].reverse().find((entry) => entry.status === "Respins") || null;
        // same type, same kind; hidden ones only when it is the current value
        this.causes = causes.filter(
          (cause) =>
            cause.module === "equipment" &&
            !!cause.creates_equipment === !!details.createsEquipment &&
            (cause.equipment_type_ids || []).includes(details.equipmentTypeId) &&
            (!cause.is_hidden || cause.id === details.requestTypeId)
        );
        if (!this.causes.some((cause) => cause.id === details.requestTypeId)) {
          const current = causes.find((cause) => cause.id === details.requestTypeId);
          this.causes.unshift(current || { id: details.requestTypeId, type: details.cause });
        }
        this.causeId = details.requestTypeId;
        if (details.createsEquipment) {
          this.modelSuggestions = suggestions;
          this.locations = locations
            .filter((loc) => !loc.is_hidden || loc.id === details.locationId)
            .map((loc) => ({ id: loc.id, label: `${loc.block}, ${loc.floor}, ${loc.room}` }))
            .sort((a, b) => a.label.localeCompare(b.label, "ro", { numeric: true }));
          this.model = details.model || null;
          this.modelSearch = details.model || null;
          this.registration = details.registration_number || "";
          this.locationId = details.locationId || null;
        }
        this.dialog = true;
        helper.scrollToTop(this);
        return true;
      } catch (error) {
        console.error("Error opening the correction:", error);
        this.$emit("error", api.errorMessage(error, "Demersul nu a putut fi încărcat. Încercați din nou."));
        return false;
      }
    },
    reset() {
      this.details = null;
      this.rejection = null;
      this.causeId = null;
      this.model = null;
      this.modelSearch = null;
      this.registration = "";
      this.locationId = null;
      this.comments = "";
      this.photos = [];
      this.errors = { causeId: "", model: "", locationId: "", comments: "" };
    },
    close() {
      this.dialog = false;
    },
    fitLocationMenu() {
      this.locationMenuWidth = this.$refs.location.$el.offsetWidth;
    },
    formatDate(value) {
      return helper.formatCreatedAt(value);
    },
    validate() {
      const isNew = this.details.createsEquipment;
      this.errors.causeId = this.causeId ? "" : "Alegeți tipul cererii";
      if (!isNew) this.errors.model = "";
      else if (!this.modelText) this.errors.model = "Scrieți modelul";
      else this.errors.model = this.modelText.length > 150 ? "Modelul este prea lung (maxim 150 de caractere)" : "";
      this.errors.locationId = !isNew || this.locationId ? "" : "Alegeți unde se livrează";
      this.errors.comments = this.comments.trim() ? "" : "Scrieți ce ați corectat";
      return !Object.values(this.errors).some(Boolean);
    },
    // only what changed is sent; the server writes the changes into the timeline
    payload() {
      const d = this.details;
      const data = { comments: this.comments.trim(), file_ids: this.photos.map((photo) => photo.id) };
      if (this.causeId !== d.requestTypeId) data.requests_type_id = this.causeId;
      if (d.createsEquipment) {
        if (this.modelText !== (d.model || "")) data.model = this.modelText;
        const registration = this.registration.trim();
        if (registration !== (d.registration_number || "")) data.registration_number = registration;
        if (this.locationId !== d.locationId) data.location_id = this.locationId;
      }
      return data;
    },
    async save() {
      if (this.saving || this.photosUploading || !this.details || !this.validate()) return;
      this.saving = true;
      try {
        await api.correctRequest(this.details.id, this.payload());
        this.$emit("success", "Demersul a fost corectat și trimis din nou Șefului departamentului");
        this.$emit("updateRequestData", this.details.id);
        this.dialog = false;
      } catch (error) {
        console.error("Error correcting request:", error);
        this.$emit("error", api.errorMessage(error, "Corectarea nu a fost salvată. Încercați din nou."));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.rejection {
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 14px;
  background: var(--brick-bg);
  border: 1px solid rgba(179, 54, 47, 0.25);
  box-shadow: inset 4px 0 0 var(--brick);
}
.rejection__head {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  color: var(--brick);
}
.rejection__head .v-icon {
  color: var(--brick);
}
.rejection__text {
  margin-top: 4px;
  color: var(--ink);
  white-space: pre-line;
  word-break: break-word;
}
</style>
